import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import docco from 'react-syntax-highlighter/dist/esm/styles/hljs/docco';
import { useAppSelector } from '../../hooks/redux-hooks';
import './style.css';

interface MarkdownRendererProps {
  markdownText: string;
}

// Custom CodeBlock component with syntax highlighting and copy functionality - memoized to prevent re-rendering
const CodeBlock: React.FC<{
  language: string;
  value: string;
  className?: string;
}> = memo(
  ({ language, value, className }) => {
    CodeBlock.displayName = 'CodeBlock';
    const [copied, setCopied] = useState(false);

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    };

    return (
      <div className="code-block-wrapper">
        <div className="code-header">
          <span className="language-label">{language}</span>
          <button className="copy-button" onClick={copyToClipboard}>
            <i className="icon-copy"></i>
            {copied ? 'Copied!' : 'Copy code'}
          </button>
        </div>
        <SyntaxHighlighter
          language={language}
          style={docco}
          customStyle={{
            margin: 0,
            borderRadius: '0 0 8px 8px',
            fontSize: '14px',
            lineHeight: '1.5',
            backgroundColor: '#f9f9f9',
            color: '#0d0d0d',
          }}
          showLineNumbers={false}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if props change
    return (
      prevProps.language === nextProps.language &&
      prevProps.value === nextProps.value &&
      prevProps.className === nextProps.className
    );
  }
);

// Custom EmailBox component for email addresses
const EmailBox: React.FC<{ href: string; children: React.ReactNode }> = memo(
  ({ href, children }) => {
    EmailBox.displayName = 'EmailBox';
    const email = href.replace('mailto:', '');

    return (
      <a
        href={href}
        className="email-box"
        onClick={(e) => {
          e.preventDefault();
          window.location.href = href;
        }}
      >
        <div className="email-box-content">
          <i className="icon-email text-[12px]"></i>
          <span className="text-[10px] ml-1">{email || ''}</span>
        </div>
      </a>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.href === nextProps.href;
  }
);

// Custom LinkBox component with favicon - commented out in favour of InlineLink
// const LinkBox: React.FC<{ href: string; children: React.ReactNode }> = memo(
//   ({ href, children }) => {
//     LinkBox.displayName = 'LinkBox';
//     const [favicon, setFavicon] = useState<string>('');
//     const [isLoading, setIsLoading] = useState(true);
//     const [websiteName, setWebsiteName] = useState<string>('');
//
//     useEffect(() => {
//       const fetchFavicon = async () => {
//         try {
//           const url = new URL(href);
//           const faviconUrl = `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=32`;
//           setFavicon(faviconUrl);
//
//           // Extract website name from hostname
//           let name = url.hostname.replace(/^www\./, '');
//           name = name.replace(/\.(com|org|net|edu|gov|mil|io|co|ai|dev)$/, '');
//
//           // Dictionary of common domain name mappings
//           const domainMappings: { [key: string]: string } = {
//             businessinsider: 'Business Insider',
//             techcrunch: 'TechCrunch',
//             stackoverflow: 'Stack Overflow',
//             github: 'GitHub',
//             linkedin: 'LinkedIn',
//             facebook: 'Facebook',
//             twitter: 'Twitter',
//             instagram: 'Instagram',
//             youtube: 'YouTube',
//             reddit: 'Reddit',
//             medium: 'Medium',
//             producthunt: 'Product Hunt',
//             hackernews: 'Hacker News',
//             wikipedia: 'Wikipedia',
//             quora: 'Quora',
//             pinterest: 'Pinterest',
//             tumblr: 'Tumblr',
//             snapchat: 'Snapchat',
//             discord: 'Discord',
//             slack: 'Slack',
//             notion: 'Notion',
//             figma: 'Figma',
//             dropbox: 'Dropbox',
//             airtable: 'Airtable',
//             zoom: 'Zoom',
//             spotify: 'Spotify',
//             netflix: 'Netflix',
//             amazon: 'Amazon',
//             google: 'Google',
//             microsoft: 'Microsoft',
//             apple: 'Apple',
//             tesla: 'Tesla',
//             uber: 'Uber',
//             airbnb: 'Airbnb',
//             stripe: 'Stripe',
//             shopify: 'Shopify',
//             squarespace: 'Squarespace',
//             wordpress: 'WordPress',
//             wix: 'Wix',
//             webflow: 'Webflow',
//             'timesofindia.indiatimes': 'Times of India',
//             indiatimes: 'India Times',
//             'economictimes.indiatimes': 'Economic Times',
//             toi: 'Times of India',
//             bbc: 'BBC',
//             cnn: 'CNN',
//             reuters: 'Reuters',
//             bloomberg: 'Bloomberg',
//             forbes: 'Forbes',
//             wsj: 'Wall Street Journal',
//             nytimes: 'New York Times',
//             washingtonpost: 'Washington Post',
//             guardian: 'The Guardian',
//             telegraph: 'The Telegraph',
//             independent: 'The Independent',
//             ft: 'Financial Times',
//             economist: 'The Economist',
//             time: 'Time',
//             newsweek: 'Newsweek',
//             usatoday: 'USA Today',
//             latimes: 'Los Angeles Times',
//             chicagotribune: 'Chicago Tribune',
//             bostonglobe: 'The Boston Globe',
//             sfgate: 'San Francisco Gate',
//             seattletimes: 'The Seattle Times',
//             denverpost: 'The Denver Post',
//             dallasnews: 'The Dallas Morning News',
//             houstonchronicle: 'Houston Chronicle',
//             miamiherald: 'Miami Herald',
//             orlandosentinel: 'Orlando Sentinel',
//             tampabay: 'Tampa Bay Times',
//             ajc: 'The Atlanta Journal-Constitution',
//             charlotteobserver: 'The Charlotte Observer',
//             newsobserver: 'The News & Observer',
//             baltimoresun: 'The Baltimore Sun',
//             philly: 'The Philadelphia Inquirer',
//             cleveland: 'The Plain Dealer',
//             detroitnews: 'The Detroit News',
//             freep: 'Detroit Free Press',
//             jsonline: 'Milwaukee Journal Sentinel',
//             startribune: 'Star Tribune',
//             omaha: 'Omaha World-Herald',
//             kansascity: 'The Kansas City Star',
//             stltoday: 'St. Louis Post-Dispatch',
//             arkansasonline: 'Arkansas Democrat-Gazette',
//             nola: 'The Times-Picayune',
//             al: 'AL.com',
//             jacksonville: 'The Florida Times-Union',
//             'sun-sentinel': 'South Florida Sun Sentinel',
//             palmbeachpost: 'The Palm Beach Post',
//             tallahassee: 'Tallahassee Democrat',
//             gainesville: 'The Gainesville Sun',
//             ocala: 'Ocala Star-Banner',
//             tbo: 'Tampa Bay Online',
//           };
//
//           // Check if we have a mapping for this domain
//           if (domainMappings[name.toLowerCase()]) {
//             name = domainMappings[name.toLowerCase()];
//           } else {
//             // Handle camelCase domains by adding spaces before capital letters
//             name = name.replace(/([a-z])([A-Z])/g, '$1 $2');
//
//             // Capitalize first letter of each word
//             name = name
//               .split(' ')
//               .map(
//                 (word) =>
//                   word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
//               )
//               .join(' ');
//           }
//
//           setWebsiteName(name);
//         } catch (error) {
//           console.error('Error fetching favicon:', error);
//           setWebsiteName('Link');
//         } finally {
//           setIsLoading(false);
//         }
//       };
//
//       fetchFavicon();
//     }, [href]);
//
//     return (
//       <a
//         href={href}
//         target="_blank"
//         rel="noopener noreferrer"
//         className="link-box"
//       >
//         <div className="link-box-content">
//           <div className="link-main">
//             {!isLoading && favicon && (
//               <img
//                 src={favicon}
//                 alt="favicon"
//                 className="link-favicon"
//                 onError={() => setFavicon('')}
//                 width={12}
//                 height={12}
//               />
//             )}
//             <span className="link-text">{websiteName}</span>
//           </div>
//         </div>
//       </a>
//     );
//   },
//   (prevProps, nextProps) => {
//     // Only re-render if href changes
//     return prevProps.href === nextProps.href;
//   }
// );

// Custom LinkBox component - inline link with underline and arrow icon
const InlineLink: React.FC<{ href: string; children: React.ReactNode; linkColor: string }> = memo(
  ({ href, children, linkColor }) => {
    InlineLink.displayName = 'InlineLink';
    const color = linkColor || '#222';

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-link"
        style={{ color, textDecorationColor: color }}
      >
        {children}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ display: 'inline', marginLeft: '2px', verticalAlign: 'middle' }}
        >
          <path d="M7 17L17 7" />
          <path d="M7 7h10v10" />
        </svg>
      </a>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.href === nextProps.href && prevProps.linkColor === nextProps.linkColor;
  }
);

// Preprocess markdown:
// 1. Escape asterisks used as bullet markers so they render as plain text
// 2. Convert [TITLE] prefixes into bold headings
const preprocessMarkdown = (text: string): string => {
  let processed = text;

  // Convert [TITLE] ... to **...** (bold heading)
  processed = processed.replace(/^\[TITLE\]\s*(.+)$/gm, '**$1**');

  // Escape leading asterisks that would become unordered list items:
  // Lines starting with * followed by a space (markdown bullet syntax)
  processed = processed.replace(/^(\s*)\*\s/gm, '$1\\* ');

  return processed;
};

// Build markdown components — needs linkColor for inline links
const buildMarkdownComponents = (linkColor: string) => ({
  ul: ({ children, ...props }: any) => (
    <ul className="markdown-list" {...props}>
      {children}
    </ul>
  ),
  li: ({ children, ...props }: any) => (
    <li className="markdown-list-item" {...props}>
      {children}
    </li>
  ),
  strong: ({ children, ...props }: any) => (
    <strong style={{ fontWeight: 700 }} {...props}>
      {children}
    </strong>
  ),
  b: ({ children, ...props }: any) => (
    <b style={{ fontWeight: 700 }} {...props}>
      {children}
    </b>
  ),
  code: ({ node, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const value = String(children).replace(/\n$/, '');
    const inline = !match;

    if (inline) {
      return (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    }

    return (
      <CodeBlock language={language} value={value} className={className} />
    );
  },
  a: ({ href, children, ...props }: any) => {
    // Check if it's an email link
    if (href && href.startsWith('mailto:')) {
      return <EmailBox href={href} {...props}>{children}</EmailBox>;
    }
    // Check if children contains an email address pattern (for autolinked emails)
    const childText = typeof children === 'string' ? children : '';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (childText && emailRegex.test(childText)) {
      return <EmailBox href={`mailto:${childText}`} {...props}>{children}</EmailBox>;
    }
    // Regular web links — render as inline underlined link with arrow
    return href ? (
      <InlineLink href={href} linkColor={linkColor} {...props}>
        {children}
      </InlineLink>
    ) : (
      <a href={href} {...props}>
        {children}
      </a>
    );
  },
});

const MarkdownRenderer: React.FC<MarkdownRendererProps> = memo(
  ({ markdownText }) => {
    MarkdownRenderer.displayName = 'MarkdownRenderer';
    const containerRef = useRef<HTMLDivElement>(null);
    const { linkColor: storeLinkColor } = useAppSelector((state) => state.common);
    const linkColor = storeLinkColor || '#222';

    const markdownComponents = useMemo(
      () => buildMarkdownComponents(linkColor),
      [linkColor]
    );

    const processedText = useMemo(
      () => preprocessMarkdown(markdownText),
      [markdownText]
    );

    useEffect(() => {
      if (containerRef.current) {
        const katexBlocks = containerRef.current.querySelectorAll(
          '.katex-display > .katex'
        );
        katexBlocks.forEach((el) => {
          el.classList.remove('too-tall');
          if (el.clientHeight > 24) {
            // Lower threshold to catch more tall equations
            el.classList.add('too-tall');
          }
        });
      }
    }, [markdownText]);

    return (
      <div className="markdown-renderer" ref={containerRef}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeKatex,
              {
                strict: false,
                throwOnError: false,
                errorColor: '#666666',
                trust: true,
                macros: {
                  '\\RR': '\\mathbb{R}',
                  '\\NN': '\\mathbb{N}',
                  '\\ZZ': '\\mathbb{Z}',
                  '\\QQ': '\\mathbb{Q}',
                  '\\CC': '\\mathbb{C}',
                },
              },
            ],
          ]}
          components={markdownComponents}
        >
          {processedText}
        </ReactMarkdown>
        <style>{`
          .katex .base, .katex .strut {
              display: initial;
          }
          .markdown-renderer {
            line-height: 1.7 !important;
            color: inherit;
            overflow: visible !important;
          }
          .markdown-renderer strong,
          .markdown-renderer b {
            font-weight: 700 !important;
          }
          /* FIX LIST RENDERING - Ensure bullet points render properly */
          .markdown-renderer ul {
            list-style-type: disc !important;
            padding-left: 2em !important;
            margin: 1em 0 !important;
          }
          .markdown-renderer ul li {
            display: list-item !important;
            margin-bottom: 0.5em !important;
            line-height: 1.6 !important;
          }
          .markdown-renderer ul li::marker {
            color: inherit !important;
            font-size: 1em !important;
          }
          /* Override any custom bullet styles
        .markdown-renderer .markdown-list {
          list-style-type: disc !important;
          padding-left: 2em !important;
        }
        .markdown-renderer .markdown-list-item {
          display: list-item !important;
          list-style-type: disc !important;
        }
        .markdown-renderer .markdown-list-item::before {
          display: none !important;
        } */
          .markdown-renderer .code-block-wrapper {
            background-color: #f9f9f9;
            border-radius: 8px;
            overflow-x: auto !important;
            overflow-y: hidden;
            margin: 1em 0;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .markdown-renderer .code-block-wrapper pre,
          .markdown-renderer .code-block-wrapper code {
            white-space: pre !important;
            word-break: normal !important;
            overflow-x: auto !important;
          }
          .markdown-renderer .code-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            border-bottom: 1px solid #ddd;
            font-size: 0.9em;
            color: #0d0d0d;
            border-radius: 8px 8px 0 0;
          }
          .markdown-renderer .language-label {
            font-weight: bold;
          }
          .markdown-renderer .copy-button {
            background: none;
            color: #0d0d0d;
            border: none;
            padding: 4px 8px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 1em;
            transition: background-color 0.2s ease;
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .markdown-renderer .copy-button .icon-copy {
            font-size: 0.9em;
          }
          /* Inline code styling */
          .markdown-renderer .inline-code {
            background: #f0f0f0;
            padding: 2px 4px;
            border-radius: 3px;
            font-family: 'Courier New', Courier, monospace;
            font-size: 0.9em;
          }
          .markdown-renderer .inline-link {
            text-decoration: underline;
            text-underline-offset: 2px;
            font-weight: 500;
            transition: opacity 0.2s ease;
          }
          .markdown-renderer .inline-link:hover {
            opacity: 0.8;
          }
          .markdown-renderer .link-box {
            display: inline-flex;
            align-items: center;
            padding: 2px 10px;
            background: #f0f0f0;
            color: #333333;
            text-decoration: none;
            transition: background-color 0.2s ease;
            gap: 4px;
            margin: 4px 0;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            max-width: fit-content;
            border-radius: 16px;
          }
          .markdown-renderer .link-box::after,
          .markdown-renderer .link-box::before {
            content: none !important;
          }
          .markdown-renderer .link-box:hover {
            background-color: #e0e0e0;
          }
          .markdown-renderer .link-box-content {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .markdown-renderer .link-main {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .markdown-renderer .link-favicon {
            width: 12px;
            height: 12px;
            border-radius: 2px;
            object-fit: contain;
            margin-top: 1px;
          }
          .markdown-renderer .link-text {
            font-size: 0.75em;
            font-weight: bold;
            color: #333333;
          }
          /* Email box styling */
          .markdown-renderer .email-box {
            display: inline-flex;
            align-items: center;
            padding: 4px 12px;
            background: #f0f0f0;
            color: #333333;
            text-decoration: none;
            transition: background-color 0.2s ease;
            gap: 6px;
            margin: 4px 0;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            max-width: fit-content;
            border-radius: 16px;
            cursor: pointer;
          }
          .markdown-renderer .email-box::after,
          .markdown-renderer .email-box::before {
            content: none !important;
          }
          .markdown-renderer .email-box:hover {
            background-color: #e0e0e0;
          }
          .markdown-renderer .email-box-content {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .markdown-renderer .email-box .icon-email {
            font-size: 14px;
            color: #666;
          }
          .markdown-renderer .email-text {
            font-size: 0.85em;
            font-weight: 500;
            color: #333333;
          }
        `}</style>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.markdownText === nextProps.markdownText;
  }
);

export default MarkdownRenderer;
